import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { ChevronRight, Gem, Gift, LogOut, Moon, Sun, UserRound } from "lucide-react";
import { useStore } from "../../stores/rootStore";
import { COMMUNITY_LINKS } from "../../lib/communityLinks";
import { formatCount } from "../../lib/format";
import { Avatar } from "../ui/Avatar";
import { CommunityIcon } from "./CommunityLinks";

export const UserMenu = observer(() => {
	const { auth, wallet, ui } = useStore();
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [open, setOpen] = useState(false);
	const rootRef = useRef<HTMLDivElement>(null);
	const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => {
		if (!open) return;
		function onPointerDown(event: PointerEvent) {
			if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
		}
		function onKeyDown(event: KeyboardEvent) {
			if (event.key === "Escape") setOpen(false);
		}
		document.addEventListener("pointerdown", onPointerDown);
		document.addEventListener("keydown", onKeyDown);
		return () => {
			document.removeEventListener("pointerdown", onPointerDown);
			document.removeEventListener("keydown", onKeyDown);
		};
	}, [open]);

	useEffect(
		() => () => {
			if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
		},
		[],
	);

	function keepOpen() {
		if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
		setOpen(true);
	}

	function scheduleClose() {
		if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
		closeTimerRef.current = setTimeout(() => setOpen(false), 220);
	}

	if (!auth.isLoggedIn) {
		return (
			<div className="flex items-center gap-1">
				<button
					type="button"
					onClick={ui.toggleTheme}
					aria-label={ui.theme === "dark" ? t("layout.lightMode") : t("layout.darkMode")}
					title={ui.theme === "dark" ? t("layout.lightMode") : t("layout.darkMode")}
					className="topbar-control muhaha-theme-toggle hidden size-8 place-items-center rounded-lg border border-transparent bg-white/[0.06] text-white/55 transition hover:border-white/12 hover:bg-white/[0.1] hover:text-white md:grid"
				>
					{ui.theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
				</button>
				<button
					type="button"
					onClick={ui.openAuth}
					className="auth-sign-in-button btn-primary h-8 rounded-lg px-3 py-1 text-xs font-semibold"
				>
					{t("common.signIn")}
				</button>
			</div>
		);
	}

	return (
		<div
			ref={rootRef}
			className="relative"
			onMouseEnter={keepOpen}
			onMouseLeave={scheduleClose}
		>
			<button
				type="button"
				onClick={() => {
					if (!auth.user) return;
					setOpen(false);
					navigate(`/u/${auth.user.handle}`);
				}}
				aria-expanded={open}
				aria-haspopup="menu"
				aria-label={t("layout.accountMenu")}
				className="user-menu-trigger grid size-8 place-items-center rounded-full border border-transparent bg-white/[0.06] transition hover:border-white/18 hover:bg-white/[0.1]"
			>
				<Avatar name={auth.displayName} src={auth.user?.avatarUrl} size={27} />
			</button>

			{open ? (
				<div
					role="menu"
					className="muhaha-user-menu absolute right-0 top-full z-40 mt-2 w-[296px] overflow-hidden rounded-[10px] border border-white/12 bg-[#171717] shadow-[0_18px_56px_rgba(0,0,0,0.58)]"
				>
					<div className="flex items-center gap-2.5 px-3.5 py-3">
						<Avatar name={auth.displayName} src={auth.user?.avatarUrl} size={32} />
						<div className="min-w-0">
							<p className="truncate text-sm font-semibold leading-5 text-white">
								{auth.displayName}
							</p>
							<p className="truncate text-xs leading-4 text-white/40">@{auth.user?.handle}</p>
						</div>
					</div>

					<button
						type="button"
						role="menuitem"
							onClick={() => {
								setOpen(false);
								navigate("/wallet");
							}}
							className="user-menu-credit flex min-h-12 w-full items-center gap-2.5 border-y border-white/10 bg-white/[0.03] px-3.5 py-2.5 text-left transition hover:bg-white/[0.065]"
						>
							<span className="user-menu-credit-icon grid size-6 shrink-0 place-items-center rounded-full bg-brand-soft text-white">
								<Gem size={13} />
							</span>
							<span className="flex min-w-0 flex-1 items-baseline gap-1.5">
								<span className="text-lg font-bold tabular-nums text-white">
									{formatCount(wallet.creditsBalance)}
								</span>
								<span className="truncate text-xs font-normal text-white/50">
									{t("wallet.creditsBalance")}
								</span>
							</span>
							<span className="grid size-6 shrink-0 place-items-center rounded-full border border-white/12 text-white/40">
								<ChevronRight size={14} />
							</span>
						</button>

						<div className="p-2">
						<MenuButton
							icon={UserRound}
							label={t("layout.myProfile")}
							onClick={() => {
								setOpen(false);
								if (auth.user) navigate(`/u/${auth.user.handle}`);
							}}
						/>
						<MenuButton
							icon={ui.theme === "dark" ? Sun : Moon}
							label={ui.theme === "dark" ? t("layout.lightMode") : t("layout.darkMode")}
							onClick={() => {
								ui.toggleTheme();
								setOpen(false);
							}}
						/>
						<MenuButton
							icon={Gift}
							label={t("rewards.inviteTitle")}
							onClick={() => {
								setOpen(false);
								navigate("/wallet?invite=1");
							}}
						/>

							<div role="group" aria-label={t("layout.community")} className="mt-1 border-t border-white/10 pt-1.5">
								<p className="px-2.5 pb-1 pt-1 text-[11px] font-medium leading-4 text-white/35">
									{t("layout.community")}
								</p>
							{COMMUNITY_LINKS.map((link) => (
								<a
									key={link.id}
									role="menuitem"
									href={link.href}
										target="_blank"
										rel="noreferrer"
									className="user-menu-community-link flex min-h-10 items-center gap-2.5 rounded-md px-2.5 text-sm font-medium text-white/60 transition hover:bg-white/[0.07] hover:text-white"
									>
										<CommunityIcon id={link.id} className="size-[17px] shrink-0" />
										{link.label}
									</a>
							))}
						</div>

							<div className="mt-1 border-t border-white/10 pt-1.5">
							<MenuButton
								icon={LogOut}
								label={t("layout.signOut")}
								danger
								disabled={auth.submitting}
								onClick={() => {
									setOpen(false);
									void auth.signOut();
								}}
							/>
						</div>
					</div>
				</div>
			) : null}
		</div>
	);
});

function MenuButton({
	icon: Icon,
	label,
	onClick,
	danger = false,
	disabled = false,
}: {
	icon: typeof UserRound;
	label: string;
	onClick: () => void;
	danger?: boolean;
	disabled?: boolean;
}) {
	return (
		<button
			type="button"
			role="menuitem"
			disabled={disabled}
			onClick={onClick}
			className={`user-menu-item ${danger ? "user-menu-item-danger" : ""} flex min-h-10 w-full items-center gap-2.5 rounded-md px-2.5 text-left text-sm font-medium transition disabled:opacity-45 ${
				danger
					? "text-white/65 hover:bg-danger/10 hover:text-danger"
					: "text-white/65 hover:bg-white/[0.07] hover:text-white"
			}`}
		>
			<Icon size={17} className="shrink-0" />
			{label}
		</button>
	);
}
